import { computed } from 'vue';
import { useHead } from '@vueuse/head';
import { useLang } from './useLang';
import { generateCSPString, CSP_CONFIG } from '@/config/csp';

/**
 * Enhanced SEO Composable with AI-friendly structured data, GEO support, and CSP
 *
 * Features:
 * - JSON-LD structured data for Person, Organization, WebSite, BreadcrumbList
 * - Geographic meta tags and LocalBusiness schema
 * - Multi-language hreflang support
 * - Advanced Open Graph and Twitter Card optimization
 * - Case study-specific structured data
 * - Content Security Policy (CSP) management
 */
export function useSEO() {
    const { locale } = useLang();

    // Site-wide constants
    const SITE_CONFIG = {
        siteName: 'Ileana Sofronie',
        siteUrl: typeof window !== 'undefined' ? window.location.origin : '',
        author: 'Ileana Sofronie',
        email: 'hello@ileana-sofronie.com', // Update with actual email
        location: {
            city: 'Köln',
            country: 'Germany',
            region: 'North Rhine-Westphalia',
            coordinates: {
                latitude: 50.9375,
                longitude: 6.9603
            }
        },
        social: {
            linkedin: 'https://www.linkedin.com/in/ileana-sofronie', // Update with actual URLs
            behance: 'https://www.behance.net/ileana-sofronie',
            dribbble: 'https://dribbble.com/ileana-sofronie'
        },
        languages: ['en', 'de']
    };

    /**
     * Generate Person schema for AI understanding
     */
    const generatePersonSchema = () => ({
        '@context': 'https://schema.org',
        '@type': 'Person',
        name: SITE_CONFIG.author,
        jobTitle: 'Product Designer',
        description: 'Accomplished product designer with a focus on innovation, aesthetics, and user-centricity',
        url: SITE_CONFIG.siteUrl,
        email: SITE_CONFIG.email,
        knowsAbout: [
            'Product Design',
            'UX Design',
            'UI Design',
            'User Experience',
            'Mobile App Design',
            'E-commerce Design',
            'Design Systems',
            'Prototyping',
            'User Research'
        ],
        address: {
            '@type': 'PostalAddress',
            addressLocality: SITE_CONFIG.location.city,
            addressCountry: SITE_CONFIG.location.country,
            addressRegion: SITE_CONFIG.location.region
        },
        geo: {
            '@type': 'GeoCoordinates',
            latitude: SITE_CONFIG.location.coordinates.latitude,
            longitude: SITE_CONFIG.location.coordinates.longitude
        },
        sameAs: Object.values(SITE_CONFIG.social)
    });

    /**
     * Generate Organization/LocalBusiness schema
     */
    const generateOrganizationSchema = () => ({
        '@context': 'https://schema.org',
        '@type': 'ProfessionalService',
        name: SITE_CONFIG.siteName,
        description: 'Product Design Services specializing in UX/UI, mobile apps, and e-commerce',
        url: SITE_CONFIG.siteUrl,
        email: SITE_CONFIG.email,
        priceRange: '$$',
        address: {
            '@type': 'PostalAddress',
            addressLocality: SITE_CONFIG.location.city,
            addressCountry: SITE_CONFIG.location.country,
            addressRegion: SITE_CONFIG.location.region
        },
        geo: {
            '@type': 'GeoCoordinates',
            latitude: SITE_CONFIG.location.coordinates.latitude,
            longitude: SITE_CONFIG.location.coordinates.longitude
        },
        areaServed: {
            '@type': 'Country',
            name: SITE_CONFIG.location.country
        }
    });

    /**
     * Generate WebSite schema for site-level search
     */
    const generateWebSiteSchema = () => ({
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: SITE_CONFIG.siteName,
        url: SITE_CONFIG.siteUrl,
        description: 'Portfolio showcasing product design work in UX/UI, mobile apps, and e-commerce',
        inLanguage: SITE_CONFIG.languages,
        author: {
            '@type': 'Person',
            name: SITE_CONFIG.author
        }
    });

    /**
     * Generate CreativeWork schema for case studies
     */
    const generateCaseStudySchema = (caseData) => {
        if (!caseData) return null;

        return {
            '@context': 'https://schema.org',
            '@type': 'CreativeWork',
            '@id': `${SITE_CONFIG.siteUrl}/${caseData.url}`,
            headline: `${caseData.title} - ${caseData.subTitle}`,
            name: caseData.title,
            description: caseData.content?.why?.content || caseData.subTitle,
            author: {
                '@type': 'Person',
                name: SITE_CONFIG.author
            },
            datePublished: caseData.tags?.[0]?.year ? `${caseData.tags[0].year}-01-01` : undefined,
            keywords: [
                caseData.tags?.[0]?.type,
                caseData.tags?.[0]?.platform,
                'Product Design',
                'UX Design',
                'UI Design'
            ].filter(Boolean).join(', '),
            image: caseData.media?.images?.main || caseData.media?.thumbnails?.first,
            url: `${SITE_CONFIG.siteUrl}/${caseData.url}`,
            inLanguage: locale.value,
            about: {
                '@type': 'Thing',
                name: caseData.tags?.[0]?.type || 'Design Project'
            }
        };
    };

    /**
     * Generate BreadcrumbList schema for navigation
     */
    const generateBreadcrumbSchema = (breadcrumbs) => {
        if (!breadcrumbs || breadcrumbs.length === 0) return null;

        return {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: breadcrumbs.map((crumb, index) => ({
                '@type': 'ListItem',
                position: index + 1,
                name: crumb.name,
                item: `${SITE_CONFIG.siteUrl}${crumb.path}`
            }))
        };
    };

    /**
     * Generate hreflang links for multi-language support
     */
    const generateHreflangLinks = (path) => {
        return SITE_CONFIG.languages.map(lang => ({
            rel: 'alternate',
            hreflang: lang,
            href: `${SITE_CONFIG.siteUrl}${path}`
        }));
    };

    /**
     * Main function to set page metadata with AI and GEO optimization
     */
    const setPageMeta = ({
        title,
        description,
        keywords,
        image = '/images/og-image.png',
        type = 'website',
        url = typeof window !== 'undefined' ? window.location.href : SITE_CONFIG.siteUrl,
        caseData = null,
        breadcrumbs = null,
        article = false,
        noindex = false,
        structuredData = []
    }) => {
        // Ensure absolute URL for image
        const absoluteImage = image.startsWith('http') ? image : `${SITE_CONFIG.siteUrl}${image}`;

        // Determine locale format (en-US, de-DE)
        const localeFormatted = locale.value === 'en' ? 'en_US' : 'de_DE';
        const alternateLocale = locale.value === 'en' ? 'de_DE' : 'en_US';

        // Build meta tags array
        const meta = [
            // Basic SEO
            { name: 'description', content: description },
            { name: 'keywords', content: keywords },

            // Open Graph
            { property: 'og:site_name', content: SITE_CONFIG.siteName },
            { property: 'og:title', content: title },
            { property: 'og:description', content: description },
            { property: 'og:image', content: absoluteImage },
            { property: 'og:image:width', content: '1200' },
            { property: 'og:image:height', content: '630' },
            { property: 'og:image:alt', content: title },
            { property: 'og:type', content: article ? 'article' : type },
            { property: 'og:url', content: url },
            { property: 'og:locale', content: localeFormatted },
            { property: 'og:locale:alternate', content: alternateLocale },

            // Twitter Card
            { name: 'twitter:card', content: 'summary_large_image' },
            { name: 'twitter:title', content: title },
            { name: 'twitter:description', content: description },
            { name: 'twitter:image', content: absoluteImage },
            { name: 'twitter:image:alt', content: title },

            // Geographic meta tags
            { name: 'geo.position', content: `${SITE_CONFIG.location.coordinates.latitude};${SITE_CONFIG.location.coordinates.longitude}` },
            { name: 'ICBM', content: `${SITE_CONFIG.location.coordinates.latitude}, ${SITE_CONFIG.location.coordinates.longitude}` },
            { name: 'geo.placename', content: `${SITE_CONFIG.location.city}, ${SITE_CONFIG.location.country}` },
            { name: 'geo.region', content: SITE_CONFIG.location.region },

            // Additional SEO
            { name: 'robots', content: noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1' },
            { name: 'viewport', content: 'width=device-width, initial-scale=1' },
            { name: 'author', content: SITE_CONFIG.author },
            { name: 'language', content: locale.value },
            { name: 'revisit-after', content: '7 days' },
            { name: 'rating', content: 'general' },

            // Mobile optimization
            { name: 'mobile-web-app-capable', content: 'yes' },
            { name: 'apple-mobile-web-app-capable', content: 'yes' },
            { name: 'apple-mobile-web-app-status-bar-style', content: 'black-translucent' },
            { name: 'apple-mobile-web-app-title', content: SITE_CONFIG.siteName },

            // AI-friendly semantic hints
            { name: 'subject', content: 'Product Design Portfolio' },
            { name: 'coverage', content: 'Worldwide' },
            { name: 'distribution', content: 'Global' },
            { name: 'category', content: 'Design, UX, UI, Product Design' },
        ];

        // Add article-specific meta tags
        if (article && caseData?.tags?.[0]?.year) {
            meta.push(
                { property: 'article:published_time', content: `${caseData.tags[0].year}-01-01` },
                { property: 'article:author', content: SITE_CONFIG.author },
                { property: 'article:section', content: caseData.tags[0].type || 'Design' },
                { property: 'article:tag', content: keywords }
            );
        }

        // Build structured data array
        const schemas = [
            generatePersonSchema(),
            generateOrganizationSchema(),
            generateWebSiteSchema()
        ];

        // Add case study schema if available
        if (caseData) {
            const caseSchema = generateCaseStudySchema(caseData);
            if (caseSchema) schemas.push(caseSchema);
        }

        // Add breadcrumb schema if available
        if (breadcrumbs) {
            const breadcrumbSchema = generateBreadcrumbSchema(breadcrumbs);
            if (breadcrumbSchema) schemas.push(breadcrumbSchema);
        }

        // Add custom structured data
        if (structuredData && structuredData.length > 0) {
            schemas.push(...structuredData);
        }

        // Build link tags
        const links = [
            { rel: 'canonical', href: url },
            ...generateHreflangLinks(typeof window !== 'undefined' ? window.location.pathname : '/')
        ];

        // Build script tags for JSON-LD
        const scripts = schemas.map(schema => ({
            type: 'application/ld+json',
            innerHTML: JSON.stringify(schema)
        }));

        // Apply head configuration
        useHead({
            title: computed(() => title),
            meta,
            htmlAttrs: {
                lang: locale.value,
                prefix: 'og: https://ogp.me/ns#'
            },
            link: links,
            script: scripts
        });
    };

    /**
     * Helper function specifically for case study pages
     */
    const setCaseStudyMeta = (caseData) => {
        const title = `${caseData.title} - ${caseData.subTitle} | ${SITE_CONFIG.siteName}`;
        const description = caseData.content?.why?.content || caseData.subTitle;
        const keywords = [
            caseData.title,
            caseData.tags?.[0]?.role,
            caseData.tags?.[0]?.type,
            caseData.tags?.[0]?.platform,
            'Product Design',
            'UX Design',
            'UI Design',
            'Case Study'
        ].filter(Boolean).join(', ');

        const image = caseData.media?.images?.main || caseData.media?.thumbnails?.first;
        const absoluteImage = image ? `${SITE_CONFIG.siteUrl}/${image}` : `${SITE_CONFIG.siteUrl}/images/og-image.png`;

        const breadcrumbs = [
            { name: 'Home', path: '/' },
            { name: caseData.title, path: `/${caseData.url}` }
        ];

        setPageMeta({
            title,
            description,
            keywords,
            image: absoluteImage,
            type: 'article',
            url: `${SITE_CONFIG.siteUrl}/${caseData.url}`,
            caseData,
            breadcrumbs,
            article: true
        });
    };

    /**
     * Helper function for the homepage
     */
    const setHomeMeta = (customTitle, customDescription, customKeywords) => {
        setPageMeta({
            title: customTitle || `${SITE_CONFIG.siteName} | Product Designer in ${SITE_CONFIG.location.city}`,
            description: customDescription || 'Accomplished product designer with a focus on innovation, aesthetics, and user-centricity. Crafting fully functional digital products that leave an indelible mark.',
            keywords: customKeywords || 'product designer, innovation, aesthetics, user-centric design, digital products, UX design, UI design, mobile apps, e-commerce, Köln, Germany',
            type: 'website',
            url: SITE_CONFIG.siteUrl
        });
    };

    /**
     * Helper function for the about page
     */
    const setAboutMeta = () => {
        setPageMeta({
            title: `About ${SITE_CONFIG.author} | Product Designer`,
            description: 'Hi, there. I\'m Ileana Sofronie. I\'ve worked on everything from mobile apps to e-commerce platforms and SaaS projects. I solve problems in ways that make people\'s lives easier.',
            keywords: 'about, product designer, UX designer, UI designer, portfolio, experience, skills, Köln, Germany',
            type: 'profile',
            url: `${SITE_CONFIG.siteUrl}/about`,
            breadcrumbs: [
                { name: 'Home', path: '/' },
                { name: 'About', path: '/about' }
            ]
        });
    };

    /**
     * Helper function for the contact page
     */
    const setContactMeta = () => {
        setPageMeta({
            title: `Contact ${SITE_CONFIG.author} | Get in Touch`,
            description: 'Feel free to reach out anytime. I\'m enthusiastic about discussing new projects and potential collaborations.',
            keywords: 'contact, get in touch, hire product designer, collaboration, projects, Köln, Germany',
            type: 'website',
            url: `${SITE_CONFIG.siteUrl}/contact`,
            breadcrumbs: [
                { name: 'Home', path: '/' },
                { name: 'Contact', path: '/contact' }
            ]
        });
    };

    /**
     * Get Content Security Policy configuration
     * @returns {Object} CSP configuration object
     */
    const getCSPConfig = () => {
        return CSP_CONFIG;
    };

    /**
     * Get CSP as a string for use in meta tags or headers
     * @returns {string} CSP policy string
     */
    const getCSPString = () => {
        return generateCSPString();
    };

    /**
     * Update CSP to allow Matomo analytics
     * Call this when user accepts cookies
     * @param {string} matomoUrl - Your Matomo instance URL (e.g., 'https://analytics.example.com/')
     */
    const enableMatomoCSP = (matomoUrl) => {
        const updatedCSP = {
            ...CSP_CONFIG,
            'script-src': [...CSP_CONFIG['script-src'], matomoUrl],
            'connect-src': [...CSP_CONFIG['connect-src'], matomoUrl],
            'img-src': [...CSP_CONFIG['img-src'], matomoUrl]
        };

        return generateCSPString(updatedCSP);
    };

    return {
        setPageMeta,
        setCaseStudyMeta,
        setHomeMeta,
        setAboutMeta,
        setContactMeta,
        getCSPConfig,
        getCSPString,
        enableMatomoCSP,
        SITE_CONFIG
    };
} 